import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  VStack,
  useToast,
  Box,
  Text,
  HStack,
} from "@chakra-ui/react";
import useAxios from "axios-hooks";
import { useEffect, useState } from "react";

import FW from "../../../../assets/img/chargePointsTab/white_fw.png";

export default function Firmware({ station }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [updateMode, setUpdateMode] = useState("url");
  const [selectedFile, setSelectedFile] = useState(null);
  const [firmwareUrl, setFirmwareUrl] = useState("");
  const toast = useToast();

  const [{ loading: uploadLoading, error: uploadError }, executeUpload] =
    useAxios(
      {
        method: "POST",
        url: "/api/ocpp16/updateFirmware/" + station?.LocationId,
      },
      { manual: true }
    );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const resetState = () => {
    setSelectedFile(null);
    setFirmwareUrl("");
  };

  const handleUpload = async () => {
    if (!station.StationId) {
      toast({
        title: "Error",
        description: "Charger ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (updateMode === "url") {
      if (!firmwareUrl.trim()) {
        toast({
          title: "Error",
          description: "Firmware URL is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const payload = {
        ChargerId: station.StationId,
        FirmwareUrl: firmwareUrl.trim(),
        RetrieveDate: new Date().toISOString(),
      };

      try {
        await executeUpload({
          data: payload,
          headers: { "Content-Type": "application/json" },
        });
        toast({
          title: "Success",
          description: "Firmware update process initiated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        resetState();
      } catch (error) {
        toast({
          title: "Failed",
          description: error.message || "An error occurred",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      if (!selectedFile) {
        toast({
          title: "Error",
          description: "Firmware file is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const formData = new FormData();
      formData.append("ChargerId", station.StationId);
      formData.append("FirmwareFile", selectedFile);
      formData.append("RetrieveDate", new Date().toISOString());

      try {
        await executeUpload({
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast({
          title: "Upload successful",
          description: "Firmware update process initiated",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        resetState();
      } catch (error) {
        toast({
          title: "Upload failed",
          description: error.message || "An error occurred during upload",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      resetState();
    }
  }, [isOpen]);

  const isSubmitDisabled =
    updateMode === "url" ? !firmwareUrl.trim() : !selectedFile;

  return (
    <div>
      <Button
        colorScheme="orange"
        bg="purple.500"
        _hover={{ bg: "purple.600" }}
        onClick={onOpen}
        borderRadius={5}
        w={"full"}
        className="acCharger_button firmware"
        leftIcon={
          <img
            src={FW}
            alt="firmware"
            style={{ display: "inline", marginRight: "0.44rem", width: "30px" }}
          />
        }
        fontStyle={"normal"}
        fontWeight={"400"}
        fontFamily={"Roboto"}
      >
        Firmware
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        motionPreset="slideInBottom"
        size="xl"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent bg="white" mx={4} borderRadius="xl" boxShadow="xl" p={2}>
          <ModalHeader fontSize="lg" fontWeight="bold" color="#f57d36">
            Update Firmware
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6}>
              <HStack spacing={4} w="100%" justifyContent="center">
                <Button
                  size="sm"
                  borderRadius="full"
                  onClick={() => setUpdateMode("url")}
                  bg={updateMode === "url" ? "#f57d36" : "gray.100"}
                  color={updateMode === "url" ? "white" : "gray.600"}
                  _hover={{
                    bg: updateMode === "url" ? "#e06420" : "gray.200",
                  }}
                  transition="all 0.2s"
                >
                  URL
                </Button>
                <Button
                  size="sm"
                  borderRadius="full"
                  onClick={() => setUpdateMode("file")}
                  bg={updateMode === "file" ? "#f57d36" : "gray.100"}
                  color={updateMode === "file" ? "white" : "gray.600"}
                  _hover={{
                    bg: updateMode === "file" ? "#e06420" : "gray.200",
                  }}
                  transition="all 0.2s"
                >
                  File Upload
                </Button>
              </HStack>

              {updateMode === "url" ? (
                <Box w="100%">
                  <Text mb={2} color="gray.600" fontSize="sm" fontWeight="medium">
                    Firmware URL
                  </Text>
                  <Input
                    placeholder="https://example.com/firmware.bin"
                    value={firmwareUrl}
                    onChange={(e) => setFirmwareUrl(e.target.value)}
                    borderColor="#f57d36"
                    _hover={{ borderColor: "#e06420" }}
                    _focus={{
                      borderColor: "#f57d36",
                      boxShadow: "0 0 0 1px #f57d36",
                    }}
                    borderRadius="md"
                    size="lg"
                  />
                </Box>
              ) : (
                <Box
                  w="100%"
                  aspectRatio={1}
                  border="2px dashed"
                  borderColor="#f57d36"
                  borderRadius="md"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{
                    borderColor: "#e06420",
                  }}
                >
                  <Input
                    type="file"
                    accept="*"
                    onChange={handleFileChange}
                    height="100%"
                    width="100%"
                    position="absolute"
                    opacity="0"
                    cursor="pointer"
                  />
                  <VStack spacing={2}>
                    <Text color="#f57d36" fontWeight="bold">
                      {selectedFile
                        ? selectedFile.name
                        : "drop firmware file here"}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      or click to browse
                    </Text>
                  </VStack>
                </Box>
              )}

              <Button
                colorScheme="orange"
                bg="#f57d36"
                onClick={handleUpload}
                isDisabled={isSubmitDisabled}
                isLoading={uploadLoading}
                width="full"
                size="lg"
                borderRadius="md"
                _hover={{
                  bg: "#e06420",
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
                transition="all 0.2s"
              >
                {updateMode === "url" ? "Send Update" : "Upload Firmware"}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}
